import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { X, Loader2, CheckCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';

// Admin modal: marks a group booking enquiry's event as complete and records
// the balance payment (manual override — Square terminal / cash / bank, or an
// online balance that wasn't auto-recognized). Calls recognizeGroupDeposit
// which moves the linked deposit Order from 'deferred' to 'recognized' AND
// sets balance_payment_status='paid' + balance fields so the online and
// manual paths converge on the same Order state.
export default function DepositRecognitionModal({ enquiry, onClose, onDone }) {
  const [balanceAmount, setBalanceAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Card / Square');
  const [paymentRef, setPaymentRef] = useState('');
  const [headcount, setHeadcount] = useState(
    enquiry?.final_headcount || enquiry?.estimated_headcount || enquiry?.number_of_drivers || 4,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const amt = Number(balanceAmount);
    if (isNaN(amt) || amt < 0) {
      setError('Please enter a valid balance amount.');
      return;
    }
    const hc = Number(headcount);
    if (!hc || hc < 4 || hc > 8) {
      setError('Driver headcount must be between 4 and 8.');
      return;
    }

    setLoading(true);
    try {
      const res = await base44.functions.invoke('recognizeGroupDeposit', {
        enquiry_id: enquiry.id,
        balance_amount: amt,
        balance_payment_method: paymentMethod,
        balance_payment_ref: paymentRef,
        final_headcount: hc,
      });
      if (res.data?.error) {
        setError(res.data.error);
      } else {
        onDone();
      }
    } catch (err) {
      setError(err.message || 'Failed to complete. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const labelClass = 'block text-white/30 text-[10px] font-bold tracking-widest mb-1.5';
  const inputClass = 'bg-[#111115] border-white/10 text-white h-11 focus:border-primary/60';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="relative w-full max-w-md bg-[#0d0d0f] border border-white/10" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors" aria-label="Close">
          <X className="w-5 h-5" />
        </button>

        <div className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-primary" />
            <span className="text-primary text-[11px] font-bold tracking-[0.3em]">MARK BALANCE PAID</span>
          </div>
          <h3 className="text-white font-black text-lg uppercase mb-1">Complete Event + Record Balance</h3>
          <p className="text-white/40 text-xs mb-5 leading-relaxed">
            Marking this event complete records the balance and moves the {enquiry.deposit_paid ? `£${Number(enquiry.deposit_amount || 0).toFixed(0)} deposit` : 'deposit'} from deferred revenue to recognized sales revenue on the next Sage export.
          </p>

          <div className="bg-[#111115] border border-white/5 p-3 mb-5 space-y-1">
            <p className="text-white/50 text-xs"><span className="text-white/30">Contact:</span> {enquiry.contact_name}</p>
            <p className="text-white/50 text-xs"><span className="text-white/30">Enquiry ref:</span> {enquiry.id.slice(-8).toUpperCase()}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={labelClass}>FINAL HEADCOUNT *</label>
              <Input
                type="number"
                min="4"
                max="8"
                value={headcount}
                onChange={(e) => setHeadcount(e.target.value)}
                className={inputClass}
                required
              />
              <p className="text-white/30 text-[10px] mt-1">Between 4 and 8 drivers.</p>
            </div>

            <div>
              <label className={labelClass}>BALANCE AMOUNT (£) *</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={balanceAmount}
                onChange={(e) => setBalanceAmount(e.target.value)}
                placeholder="e.g. 150.00"
                className={inputClass}
                required
              />
            </div>

            <div>
              <label className={labelClass}>PAYMENT METHOD</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="seo-input h-11 bg-[#111115] border border-white/10"
              >
                <option value="Card / Square">Card / Square (Terminal)</option>
                <option value="Cash">Cash</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>PAYMENT REFERENCE</label>
              <Input
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                placeholder="Square payment ID, bank ref, etc."
                className={inputClass}
              />
            </div>

            {error && <p className="text-red-400 text-xs font-medium">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm tracking-widest py-3.5 transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              MARK BALANCE PAID
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}